/**
 * Pure alignment + comparison logic for the Compare Runs feature.
 *
 * Everything here is framework-free and unit-testable: it turns N benchmark runs and
 * their per-case results into one matrix (one row per logically-shared case, one
 * column per run), plus the derived divergence / delta / winner data the UI renders.
 * Keeping it pure keeps the React components thin.
 */
import type {
  BenchmarkRun,
  BenchmarkResult,
} from "@/lib/api/resources/benchmark";

/** Colour-blind-safe palette assigned to runs in selection order (indigo/emerald/amber/pink). */
export const RUN_COLORS = ["#4f46e5", "#059669", "#d97706", "#db2777"];
export const MAX_COMPARE_RUNS = RUN_COLORS.length;

export interface CompareRun {
  /** Bare run UUID (the value carried in the `runs` URL param). */
  id: string;
  color: string;
  label: string;
  run: BenchmarkRun;
}

export interface CompareRow {
  /** Join key (test-case id or normalised input reference). */
  key: string;
  /** Display label (basename / test-case name). */
  label: string;
  reference: string | null;
  /** runId -> result for this case, or null when the run did not include it. */
  cells: Record<string, BenchmarkResult | null>;
  /** Runs that have this case disagree (verdict, judge span >= 2, or different output). */
  divergent: boolean;
  /** Compact verdict glyphs across runs in column order, e.g. "✓ ✗ —". */
  pattern: string;
  hasError: boolean;
}

export interface CompareMatrix {
  rows: CompareRow[];
  /** Results that could not be aligned (no test-case id and no input reference). */
  unalignable: number;
  /** Rows present in every selected run. */
  sharedCount: number;
}

export interface Compatibility {
  ok: boolean;
  warnings: string[];
}

export const isErrored = (r: BenchmarkResult | null | undefined): boolean =>
  !!(r && r.actualOutput && (r.actualOutput as Record<string, unknown>).error);

/**
 * Normalise an input reference into a stable join key: slashes unified and trimmed,
 * but the FULL relative path and its case are preserved. Stripping the directory or
 * lowercasing would silently merge unrelated cases (positive/x.jpg vs negative/x.jpg,
 * or Batch1/001.jpg vs batch1/001.jpg on a case-sensitive store).
 */
export const normalizeRef = (ref: string | null | undefined): string | null => {
  if (!ref) return null;
  const v = ref
    .replace(/\\/g, "/")
    .replace(/\/{2,}/g, "/")
    .trim();
  return v.length ? v : null;
};

/** The join key for a result; null when it cannot be aligned (never falls back to r.id). */
export const caseKey = (r: BenchmarkResult): string | null =>
  r.testCase?.id ?? normalizeRef(r.inputReference);

/**
 * Canonical, key-sorted JSON string used for structural equality (not cryptographic).
 * Only ever compared between runs in the same session, so encoding quirks don't matter —
 * intra-session determinism does.
 */
export const canonical = (value: unknown): string => {
  const seen = new WeakSet<object>();
  const norm = (v: unknown): unknown => {
    if (v === null || typeof v !== "object") return v;
    if (seen.has(v as object)) return null;
    seen.add(v as object);
    if (Array.isArray(v)) return v.map(norm);
    return Object.keys(v as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = norm((v as Record<string, unknown>)[k]);
        return acc;
      }, {});
  };
  try {
    return JSON.stringify(norm(value));
  } catch {
    return "";
  }
};

const shortLabel = (ref: string | null, fallback: string): string =>
  (ref?.split("/").pop() || fallback || "").toString();

/** Assign runs their palette colour + display label, capped at MAX_COMPARE_RUNS. */
export const buildRuns = (
  entries: { id: string; run: BenchmarkRun }[],
): CompareRun[] =>
  entries.slice(0, MAX_COMPARE_RUNS).map((e, i) => ({
    id: e.id,
    run: e.run,
    color: RUN_COLORS[i % RUN_COLORS.length],
    label: e.run.benchmarkConfig?.name || e.run.agent?.name || `Run ${i + 1}`,
  }));

const verdictGlyph = (r: BenchmarkResult | null): string => {
  if (r == null) return "—";
  if (isErrored(r)) return "⚠";
  if (r.correct === true) return "✓";
  if (r.correct === false) return "✗";
  return "·";
};

const computeRowDivergence = (row: CompareRow, runs: CompareRun[]): void => {
  const present = runs
    .map((cr) => row.cells[cr.id])
    .filter((r): r is BenchmarkResult => r != null);
  row.pattern = runs.map((cr) => verdictGlyph(row.cells[cr.id])).join(" ");
  row.hasError = present.some(isErrored);

  if (present.length < 2) {
    row.divergent = false;
    return;
  }
  const corrects = new Set(present.map((r) => r.correct));
  const scores = present
    .map((r) => r.qualityScore)
    .filter((s): s is number => s != null);
  const scoreSpan =
    scores.length >= 2 ? Math.max(...scores) - Math.min(...scores) : 0;
  // Structural inequality matters even when verdict + judge agree: two different
  // outputs the judge happened to score identically are still a real divergence.
  const outputs = new Set(present.map((r) => canonical(r.actualOutput)));
  row.divergent = corrects.size > 1 || scoreSpan >= 2 || outputs.size > 1;
};

/** Build the comparison matrix from runs + their result lists. */
export const alignResults = (
  runs: CompareRun[],
  resultsByRun: Record<string, BenchmarkResult[]>,
): CompareMatrix => {
  const rowMap = new Map<string, CompareRow>();
  let unalignable = 0;

  for (const cr of runs) {
    for (const r of resultsByRun[cr.id] ?? []) {
      const key = caseKey(r);
      if (!key) {
        unalignable += 1;
        continue;
      }
      let row = rowMap.get(key);
      if (!row) {
        row = {
          key,
          label: shortLabel(r.inputReference, r.testCase?.name ?? key),
          reference: r.inputReference,
          cells: {},
          divergent: false,
          pattern: "",
          hasError: false,
        };
        rowMap.set(key, row);
      }
      // One result per (run, case); keep the first if a run somehow has duplicates.
      if (!(cr.id in row.cells)) row.cells[cr.id] = r;
    }
  }

  const rows = Array.from(rowMap.values());
  for (const row of rows) {
    for (const cr of runs) if (!(cr.id in row.cells)) row.cells[cr.id] = null;
    computeRowDivergence(row, runs);
  }

  const sharedCount = rows.filter((row) =>
    runs.every((cr) => row.cells[cr.id] != null),
  ).length;

  // Divergent cases first (that is what the user came to see), then alphabetical.
  rows.sort((a, b) =>
    a.divergent === b.divergent
      ? a.label.localeCompare(b.label)
      : a.divergent
        ? -1
        : 1,
  );

  return { rows, unalignable, sharedCount };
};

/* ------------------------------------------------------------------ metrics --- */

export type MetricKey =
  | "accuracyRate"
  | "averageQualityScore"
  | "successRate"
  | "averageLatencyMs"
  | "p95LatencyMs"
  | "totalTokens"
  | "totalCost";

export type MetricCategory =
  | "Quality & Accuracy"
  | "Performance"
  | "Cost & Efficiency";

export interface MetricDef {
  key: MetricKey;
  label: string;
  higherIsBetter: boolean;
  category: MetricCategory;
  format: (v: number | null) => string;
}

const pct = (v: number | null): string =>
  v == null ? "—" : `${(v * 100).toFixed(1)}%`;
const ms = (v: number | null): string =>
  v == null ? "—" : `${Math.round(v)} ms`;
const num = (v: number | null): string =>
  v == null ? "—" : v.toLocaleString();
const money = (v: number | null): string => {
  if (v == null) return "—";
  if (v === 0) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(v);
};

export const METRICS: MetricDef[] = [
  {
    key: "accuracyRate",
    label: "Accuracy",
    higherIsBetter: true,
    category: "Quality & Accuracy",
    format: pct,
  },
  {
    key: "averageQualityScore",
    label: "Avg Judge",
    higherIsBetter: true,
    category: "Quality & Accuracy",
    format: (v) => (v == null ? "—" : `${v.toFixed(1)}/10`),
  },
  {
    key: "successRate",
    label: "Success rate",
    higherIsBetter: true,
    category: "Quality & Accuracy",
    format: pct,
  },
  {
    key: "averageLatencyMs",
    label: "Avg latency",
    higherIsBetter: false,
    category: "Performance",
    format: ms,
  },
  {
    key: "p95LatencyMs",
    label: "P95 latency",
    higherIsBetter: false,
    category: "Performance",
    format: ms,
  },
  {
    key: "totalTokens",
    label: "Total tokens",
    higherIsBetter: false,
    category: "Cost & Efficiency",
    format: num,
  },
  {
    key: "totalCost",
    label: "Total cost",
    higherIsBetter: false,
    category: "Cost & Efficiency",
    format: money,
  },
];

/** Read a metric off a run, deriving the ones not stored directly. */
export const runMetric = (run: BenchmarkRun, key: MetricKey): number | null => {
  if (key === "successRate") {
    return run.totalCases > 0
      ? (run.completedCases - run.failedCases) / run.totalCases
      : null;
  }
  const v = (run as unknown as Record<string, number | null>)[key];
  return v ?? null;
};

export interface Delta {
  diff: number;
  /** true = improvement vs baseline, false = regression, null = unchanged. */
  better: boolean | null;
}

export const metricDelta = (
  def: MetricDef,
  value: number | null,
  baseline: number | null,
): Delta | null => {
  if (value == null || baseline == null) return null;
  const diff = value - baseline;
  if (diff === 0) return { diff: 0, better: null };
  return { diff, better: def.higherIsBetter ? diff > 0 : diff < 0 };
};

/**
 * Id of the run that wins a metric, ignoring runs that lack the value (never crown on absent data).
 * A metric is only won by a *strict, unique* best: when two or more runs share the extreme value the
 * result is a tie and no run leads it, so we return null. Otherwise a tie would be silently credited to
 * whichever run happens to be listed first, wrongly inflating its "leads on" list and overall-win count.
 */
export const bestRunId = (
  def: MetricDef,
  runs: CompareRun[],
): string | null => {
  let best: { id: string; v: number } | null = null;
  let tied = false;
  for (const cr of runs) {
    const v = runMetric(cr.run, def.key);
    if (v == null) continue;
    if (best == null || (def.higherIsBetter ? v > best.v : v < best.v)) {
      best = { id: cr.id, v };
      tied = false;
    } else if (v === best.v) {
      tied = true;
    }
  }
  return tied ? null : (best?.id ?? null);
};

/** Non-blocking compatibility warnings (different type / input / low overlap). */
export const checkCompatibility = (
  runs: CompareRun[],
  matrix: CompareMatrix,
): Compatibility => {
  const warnings: string[] = [];
  if (runs.length < 2) return { ok: true, warnings };

  const cfg = (cr: CompareRun) =>
    (cr.run.config ?? {}) as Record<string, unknown>;
  const types = new Set(runs.map((cr) => String(cfg(cr).type ?? "?")));
  if (types.size > 1) {
    warnings.push(
      "Runs use different benchmark types — matched cases may be unrelated.",
    );
  }
  const inputs = new Set(runs.map((cr) => canonical(cfg(cr).input ?? {})));
  if (inputs.size > 1) {
    warnings.push(
      "Runs use different input sources — case overlap may be limited.",
    );
  }
  if (matrix.rows.length > 0 && matrix.sharedCount / matrix.rows.length < 0.5) {
    warnings.push(
      `Only ${matrix.sharedCount} of ${matrix.rows.length} cases are shared by all runs (different samples or limits).`,
    );
  }
  return { ok: warnings.length === 0, warnings };
};
